// 掉落配置（EP-006-S2 / GDD 01 §⑤.3）—— 全工程**唯一**允许出现掉落表数字的文件。
// 其它任何文件（roll / store / UI）需要掉落相关数值都必须从这里取，不得硬编码。
//
// 数值来源：Config B（GDD 01 §5.3 已拍板）：
//   四档边际目标 N:60 / U:27 / R:8 / L:5（归一化前，池内权重之和恒为 100）。
//   件数 countWeights = 84/13/3（1 件 / 2 件 / 3 件）。
//   目的地 destinationWeights = 30/30/20/20（riverside / forest / hill / town）。
//   传说保护 legendaryProtectionTrips = 10（前 10 次旅行传说档清零）。
//   同物上限 sameGiftMaxPerTrip = 2。
//
// 设计决策（落地记录见 production/CHANGELOG.md）：
//   MVP 批次 1 四个目的地**共用同一份 Config B 池**，保证「N > U > R > L」倒挂断言
//   在任何目的地 / 任何三角色（常规 / 长旅行 / 保护期）下无条件成立。目的地主题化分池
//   与长旅行倾斜（longTripRareBoost / longTripLegendBoost）为后续批次预留，本批次暂不动。
import type { DestinationId } from '../../save/types';

export interface CountWeight {
  k: number; // 掉落件数
  w: number; // 件数权重
}

export interface DropTuning {
  sameGiftMaxPerTrip: number;
  legendaryProtectionTrips: number;
  longTripRareBoost: number; // 预留：长旅行稀有档加权（本批次 roll 未消费）
  longTripLegendBoost: number; // 预留：长旅行传说档加权（本批次 roll 未消费）
}

export interface DropConfig {
  pools: Record<DestinationId, Record<string, number>>;
  destinationWeights: Record<DestinationId, number>;
  countWeights: CountWeight[];
  tuning: DropTuning;
}

// Config B 池内权重：5N / 2U / 2R / 1L，组权和 = 60 / 27 / 8 / 5（= N:U:R:L 目标；明信片 v2 起移除，
// U 档 3 件 → 2 件；单件权重 9 → 13.5，保持 U 档整体占比 27% 不变，target 不动）。
const N_W = 12; // 5 件 × 12 = 60
const U_W = 13.5; // 2 件 × 13.5 = 27（v1: 3 件 × 9 = 27；明信片移除后单件权重升 13.5）
const R_W = 4; // 2 件 × 4  = 8
const L_W = 5; // 1 件 × 5  = 5

// 单一共享池：MVP 批次 1 各目的地共用，保证四档倒挂断言无条件成立。
const CONFIG_B_POOL: Record<string, number> = {
  twig: N_W,
  pebble: N_W,
  maple_bookmark: N_W,
  mushroom: N_W,
  marshmallow: N_W,
  shell: U_W,
  pinecone: U_W,
  honey_jar: R_W,
  wind_chime: R_W,
  star_bottle: L_W,
};

export const dropConfig: DropConfig = {
  pools: {
    riverside: { ...CONFIG_B_POOL },
    forest: { ...CONFIG_B_POOL },
    hill: { ...CONFIG_B_POOL },
    town: { ...CONFIG_B_POOL },
  },
  destinationWeights: {
    riverside: 30,
    forest: 30,
    hill: 20,
    town: 20,
  },
  countWeights: [
    { k: 1, w: 84 },
    { k: 2, w: 13 },
    { k: 3, w: 3 },
  ],
  tuning: {
    sameGiftMaxPerTrip: 2,
    legendaryProtectionTrips: 10,
    longTripRareBoost: 1.5,
    longTripLegendBoost: 2.0,
  },
};

// 兜底池：pools[destination] 命中不到时使用（当前四目的地都已配置，仅作防御）。
export const FALLBACK_POOL: Record<string, number> = { ...CONFIG_B_POOL };
